import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Wrench,
  Package,
  ScanLine,
  Users,
  ShieldCheck,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { BrandMark } from './BrandMark';
import type { Rol } from '@/types/auth';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: Rol[];
}

/**
 * Items de navegación principal — filtrados por rol.
 *
 * Convención de orden: alta-frecuencia primero (Dashboard, Mantenimiento),
 * gestión después (Inventario, Accesos), administración al final.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/',                label: 'Dashboard',    icon: LayoutDashboard, roles: ['ADMIN', 'SOPORTE', 'COMPRAS'] },
  { to: '/mantenimiento',   label: 'Mantenimiento',icon: Wrench,          roles: ['ADMIN', 'SOPORTE'] },
  { to: '/inventario',      label: 'Inventario',   icon: Package,         roles: ['ADMIN', 'SOPORTE', 'COMPRAS'] },
  { to: '/accesos',         label: 'Accesos',      icon: ScanLine,        roles: ['ADMIN', 'SOPORTE', 'PEOPLE'] },
  { to: '/colaboradores',   label: 'Colaboradores',    icon: Users,           roles: ['ADMIN', 'SOPORTE', 'PEOPLE'] },
];

const ADMIN_ITEMS: NavItem[] = [
  { to: '/admin/usuarios', label: 'Usuarios',  icon: ShieldCheck, roles: ['ADMIN'] },
  { to: '/admin/sync',     label: 'Sincronización', icon: Settings, roles: ['ADMIN'] },
];

export function Sidebar() {
  const { user } = useAuthStore();
  if (!user) return null;

  const items = NAV_ITEMS.filter((i) => i.roles.includes(user.rol));
  const adminItems = ADMIN_ITEMS.filter((i) => i.roles.includes(user.rol));

  return (
    <aside className="hidden md:flex md:flex-col w-64 shrink-0 bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-sidebar-border">
        <BrandMark className="shadow-lg shadow-primary-900/40" />
        <div className="leading-tight">
          <p className="font-bold text-base tracking-tight">Master Balance</p>
          <p className="text-[10px] uppercase tracking-widest text-sidebar-foreground/60">Panel de Gestión</p>
        </div>
      </div>

      {/* Nav principal */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <p className="px-3 mb-2 text-[10px] uppercase tracking-widest text-sidebar-foreground/50 font-semibold">
          Operación
        </p>
        <ul className="space-y-0.5">
          {items.map((it) => (
            <SidebarItem key={it.to} item={it} />
          ))}
        </ul>

        {adminItems.length > 0 && (
          <>
            <p className="px-3 mt-6 mb-2 text-[10px] uppercase tracking-widest text-sidebar-foreground/50 font-semibold">
              Administración
            </p>
            <ul className="space-y-0.5">
              {adminItems.map((it) => (
                <SidebarItem key={it.to} item={it} />
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* Footer / signature corporativa */}
      <div className="border-t border-sidebar-border px-5 py-3 text-[10px] text-sidebar-foreground/40">
        <p>v0.1.0 — Sprint 4</p>
        <p>© {new Date().getFullYear()} GSH</p>
      </div>
    </aside>
  );
}

function SidebarItem({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-inner ring-1 ring-inset ring-white/5'
              : 'text-sidebar-foreground/80',
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Indicador lateral activo */}
            <span
              className={cn(
                'absolute left-0 h-6 w-0.5 rounded-r-full bg-primary-300 transition-opacity',
                isActive ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden
            />
            <Icon
              className={cn(
                'h-4 w-4 transition-colors',
                isActive ? 'text-white' : 'text-sidebar-foreground/60 group-hover:text-white',
              )}
            />
            <span>{item.label}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}
